import React, { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LiaEyeSlashSolid, LiaEyeSolid } from "react-icons/lia";
import { FaStore, FaChartLine, FaShieldAlt, FaTruck } from "react-icons/fa";
import MarketNavbar from "../MarketNav/MarketNavbar";
import MarketplacePage from "../components/MarketplacePage";
import Spinner from "../../Spinner/Spinner";
import { AuthContext } from "../../../Context/AuthContext";
import "./MerchantForm.css";

const defaultForm = {
  first_name: "",
  last_name: "",
  email: "",
  phone_number: "",
  password: "",
  country_code: "+234",
};

const BENEFITS = [
  {
    icon: FaStore,
    title: "Your own storefront",
    text: "List products, manage inventory, and reach buyers across Nigeria on STEX.",
  },
  {
    icon: FaChartLine,
    title: "Grow your sales",
    text: "Get discovered through trending deals, categories, and marketplace search.",
  },
  {
    icon: FaTruck,
    title: "Built-in logistics",
    text: "Use STEX shipping options so customers can track orders with confidence.",
  },
  {
    icon: FaShieldAlt,
    title: "Secure onboarding",
    text: "Verify your email, activate your account, and start selling in a few steps.",
  },
];

const STEPS = ["Create account", "Verify email", "Start selling"];

const MerchantForm = () => {
  const { token } = useContext(AuthContext);
  const [formInput, setFormInput] = useState(defaultForm);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { first_name, last_name, email, phone_number, password } = formInput;
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormInput({ ...formInput, [name]: value });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const headers = { "Content-Type": "application/json" };
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/users/register/merchant`, {
        method: "POST",
        headers,
        body: JSON.stringify(formInput),
      });

      const resData = await response.json();

      if (!response.ok) {
        throw new Error(resData.message || "Registration failed");
      }

      setFormInput(defaultForm);
      if (resData.autoActivated) {
        setSuccessMsg("Account created! You can sign in now.");
        setTimeout(() => navigate("/Login"), 1200);
      } else {
        setSuccessMsg("Account created! Check your email for the activation code.");
        setTimeout(() => navigate("/EmailVerificationForm"), 1200);
      }
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <MarketNavbar />
      <MarketplacePage className="merchant-signup-page">
        <nav className="merchant-signup-breadcrumb" aria-label="Breadcrumb">
          <Link to="/market">Home</Link>
          <span>/</span>
          <span>Become a merchant</span>
        </nav>

        <div className="merchant-signup-layout">
          <section className="merchant-signup-hero">
            <div className="merchant-signup-hero__badge">Sell on STEX</div>
            <h1>Turn your products into profit</h1>
            <p>
              Join Smart Trans Express marketplace and connect with customers looking for
              quality products — from groceries and fashion to electronics and more.
            </p>

            <ol className="merchant-signup-steps">
              {STEPS.map((step, index) => (
                <li key={step}>
                  <span className="merchant-signup-steps__num">{index + 1}</span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>

            <ul className="merchant-signup-benefits">
              {BENEFITS.map(({ icon: Icon, title, text }) => (
                <li key={title}>
                  <span className="merchant-signup-benefits__icon" aria-hidden="true">
                    <Icon />
                  </span>
                  <div>
                    <strong>{title}</strong>
                    <p>{text}</p>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          <section className="merchant-signup-card">
            <header className="merchant-signup-card__header">
              <h2>Create merchant account</h2>
              <p>Fill in your details to register. We will send a verification code to your email.</p>
            </header>

            <form className="merchant-signup-form" onSubmit={handleSubmit}>
              <div className="merchant-signup-form__row">
                <label className="merchant-signup-field">
                  <span>First name</span>
                  <input
                    type="text"
                    name="first_name"
                    value={first_name}
                    onChange={handleChange}
                    placeholder="John"
                    required
                    autoComplete="given-name"
                  />
                </label>
                <label className="merchant-signup-field">
                  <span>Last name</span>
                  <input
                    type="text"
                    name="last_name"
                    value={last_name}
                    onChange={handleChange}
                    placeholder="Doe"
                    required
                    autoComplete="family-name"
                  />
                </label>
              </div>

              <label className="merchant-signup-field">
                <span>Email address</span>
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                />
              </label>

              <label className="merchant-signup-field">
                <span>Phone number</span>
                <div className="merchant-signup-phone">
                  <span className="merchant-signup-phone__code">+234</span>
                  <input
                    type="tel"
                    name="phone_number"
                    value={phone_number}
                    onChange={handleChange}
                    placeholder="801 234 5678"
                    required
                    autoComplete="tel-national"
                  />
                </div>
              </label>

              <label className="merchant-signup-field">
                <span>Password</span>
                <div className="merchant-signup-pass">
                  <input
                    type={showPass ? "text" : "password"}
                    name="password"
                    value={password}
                    onChange={handleChange}
                    placeholder="Create a strong password"
                    required
                    autoComplete="new-password"
                    minLength={6}
                  />
                  <button
                    type="button"
                    className="merchant-signup-pass__toggle"
                    onClick={() => setShowPass(!showPass)}
                    aria-label={showPass ? "Hide password" : "Show password"}
                  >
                    {showPass ? <LiaEyeSlashSolid /> : <LiaEyeSolid />}
                  </button>
                </div>
              </label>

              {error && (
                <p className="merchant-signup-alert merchant-signup-alert--error" role="alert">
                  {error}
                </p>
              )}
              {successMsg && (
                <p className="merchant-signup-alert merchant-signup-alert--success" role="status">
                  {successMsg}
                </p>
              )}

              <button type="submit" className="merchant-signup-submit" disabled={isLoading}>
                {isLoading ? <Spinner /> : "Create merchant account"}
              </button>
            </form>

            <footer className="merchant-signup-card__footer">
              <p>
                Already have an account? <Link to="/Login">Sign in</Link>
              </p>
              <Link to="/market" className="merchant-signup-back">
                ← Back to marketplace
              </Link>
            </footer>
          </section>
        </div>
      </MarketplacePage>
    </>
  );
};

export default MerchantForm;
